import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { BeakerIcon, HeartIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { patchItems } from '@/components/Patches/PatchSearchItems';
import { DATASETS } from '@/components/Constants';
import { userAppContext } from '@/context/Context';
import { useSearchTabNavigation } from '@/hooks/search/useSearchTabNavigation';
import SharedButton from '@/global/Button/Button';
import { buildSearchTabTitle } from '@/utils/searchTabTitle';
import type { Section as SectionType } from '@/types';
import { UserAppContextType } from '@/router/types';
import { fetchData } from '../searchBar/apiCalls';
import {
  ALL_RESULTS_TAB,
  buildNavigableEntries,
  getDropdownTabs,
  getSelectionTagLabel,
  getVisibleSections,
  handleSearchItems,
  rankSectionsByRelevance,
  resolveActiveTab,
} from '../searchBar/helpers';
import { SearchSpotlightItem } from './SearchSpotlightItem';
import {
  SPOTLIGHT_DEBOUNCE_MS,
  SPOTLIGHT_MIN_QUERY_LENGTH,
  type SpotlightSearchItem,
  buildSpotlightSearchQuery,
  openSpotlightSearch,
  toggleSpotlightSelection,
} from './searchSpotlightHelpers';

const CategoryIcon = ({ category }: { category: string }) => {
  const className = 'search-dropdown-cat-icon';
  if (category === DATASETS.DISEASES) return <HeartIcon className={className} aria-hidden />;
  if (category === DATASETS.GENES) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M7 4c4 4 6 12 10 16M17 4c-4 4-6 12-10 16M8.5 9h7M8.5 15h7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (category === DATASETS.VARIANTS) return <SparklesIcon className={className} aria-hidden />;
  if (category === DATASETS.CHEMICALS) return <BeakerIcon className={className} aria-hidden />;
  return null;
};

type SearchSpotlightResultsProps = {
  query: string;
  onNavigated: () => void;
  onKeyDownRef?: React.MutableRefObject<((event: ReactKeyboardEvent) => void) | null>;
};

export function SearchSpotlightResults({ query, onNavigated, onKeyDownRef }: SearchSpotlightResultsProps) {
  const { navigateSearch } = useSearchTabNavigation();
  const { authorization, isAuthenticated, restrictSources, groups } =
    (userAppContext() as unknown as UserAppContextType) ?? {};
  const [data, setData] = useState<any>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(ALL_RESULTS_TAB);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [selectedItems, setSelectedItems] = useState<SpotlightSearchItem[]>([]);
  const cancelTokenRef = useRef<any>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const trimmed = query.trim();
  const canSearch = trimmed.length >= SPOTLIGHT_MIN_QUERY_LENGTH;
  const selectedCount = selectedItems.length;

  const noData =
    !data ||
    (data.diseases?.length === 0 &&
      data.genes?.length === 0 &&
      data.variants?.length === 0 &&
      data.chemicals?.length === 0);

  const sections: SectionType[] = noData
    ? []
    : rankSectionsByRelevance(handleSearchItems(selectedItems, data), trimmed);
  const resolvedTab = resolveActiveTab(sections, activeTab);
  const tabs = getDropdownTabs(sections);
  const visibleSections = getVisibleSections(sections, resolvedTab);
  const navigableEntries = buildNavigableEntries(visibleSections);
  const showTabs = tabs.length > 1;
  const showResults = canSearch && !isLoading && !error && !noData;

  const runSearch = useCallback(
    (items: SpotlightSearchItem[]) => {
      if (items.length === 0) return;
      const search = buildSpotlightSearchQuery(items, { restrictSources, groups });
      openSpotlightSearch(navigateSearch, {
        search,
        title: items.length === 1 ? getSelectionTagLabel(items[0]) : buildSearchTabTitle(items),
        searchItems: items,
      });
      onNavigated();
    },
    [groups, navigateSearch, onNavigated, restrictSources],
  );

  const toggleItem = useCallback((item: any) => {
    setSelectedItems(prev => toggleSpotlightSelection(prev, item));
  }, []);

  const goToItem = useCallback(
    (item: any) => {
      runSearch([patchItems(item)]);
    },
    [runSearch],
  );

  useEffect(() => {
    if (!canSearch) {
      setData(undefined);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    const timeoutId = setTimeout(() => {
      fetchData({
        cancelToken: cancelTokenRef,
        authorization,
        isAuthenticated,
        value: trimmed,
      })
        .then(payload => {
          setData(payload);
        })
        .catch((err: any) => {
          setError(err?.message ?? 'Error');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, SPOTLIGHT_DEBOUNCE_MS);

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Cleanup');
    }

    return () => clearTimeout(timeoutId);
  }, [trimmed, canSearch, authorization, isAuthenticated]);

  useEffect(() => {
    setActiveTab(ALL_RESULTS_TAB);
    setFocusedIndex(0);
  }, [trimmed]);

  useEffect(() => {
    setActiveTab(resolvedTab);
    setFocusedIndex(0);
  }, [resolvedTab]);

  useEffect(() => {
    const focusedNode = listRef.current?.querySelector(`[data-nav-index="${focusedIndex}"]`);
    if (focusedNode instanceof HTMLElement) {
      focusedNode.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, resolvedTab]);

  useEffect(() => {
    const handleKeys = (event: ReactKeyboardEvent) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        if (selectedCount > 0) {
          event.preventDefault();
          runSearch(selectedItems);
        }
        return;
      }

      if (!showResults || navigableEntries.length === 0) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        setFocusedIndex(current => (current + delta + navigableEntries.length) % navigableEntries.length);
        return;
      }

      if (event.key !== 'Enter') return;

      const entry = navigableEntries[focusedIndex];
      if (!entry) return;
      event.preventDefault();
      if (entry.type === 'seeAll') {
        setActiveTab(entry.sectionTitle);
        return;
      }
      if (selectedCount > 0) {
        toggleItem(entry.item);
        return;
      }
      goToItem(entry.item);
    };

    if (onKeyDownRef) {
      onKeyDownRef.current = handleKeys;
    }
    return () => {
      if (onKeyDownRef) onKeyDownRef.current = null;
    };
  }, [
    showResults,
    navigableEntries,
    focusedIndex,
    onKeyDownRef,
    selectedCount,
    selectedItems,
    runSearch,
    toggleItem,
    goToItem,
  ]);

  const footer =
    selectedCount > 0 ? (
      <div className="search-dropdown-footer">
        <div className="search-dropdown-footer__bar">
          <div className="search-dropdown-selection__tags" aria-label="Selected items">
            {selectedItems.map((item, index) => {
              const patched = patchItems(item);
              const label = getSelectionTagLabel(patched);
              return (
                <button
                  key={patched.id ?? index}
                  type="button"
                  className="search-dropdown-chip"
                  data-category={patched.dataset}
                  data-testid="search-spotlight-chip"
                  title={label}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => toggleItem(patched)}
                >
                  <span className="search-dropdown-chip__dot" aria-hidden />
                  <span className="search-dropdown-chip__label">{label}</span>
                  <XMarkIcon className="search-dropdown-chip__remove" aria-hidden />
                  <span className="search-spotlight-sr-only">Remove {label}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="search-dropdown-clear-all"
            onMouseDown={event => event.preventDefault()}
            onClick={() => setSelectedItems([])}
          >
            Clear all
          </button>
          <SharedButton
            variant="primary"
            size="sm"
            className="search-dropdown-search-btn"
            data-testid="search-spotlight-run"
            onClick={() => runSearch(selectedItems)}
          >
            Search {selectedCount} selected
          </SharedButton>
        </div>
      </div>
    ) : (
      <div className="search-spotlight-hint">
        <span>Click to add several of the same type · Enter opens the focused result</span>
        <span>Esc to close</span>
      </div>
    );

  if (!canSearch) {
    return (
      <div className="search-spotlight-results">
        <span className="search-dropdown-status">
          Type at least {SPOTLIGHT_MIN_QUERY_LENGTH} characters to search genes, diseases, variants and
          chemicals.
        </span>
        {footer}
      </div>
    );
  }

  let navCursor = 0;

  return (
    <div className="search-spotlight-results">
      {showTabs && (
        <div className="search-dropdown-tabs" role="tablist" aria-label="Result categories">
          {tabs.map(tab => {
            const selected = tab.id === resolvedTab;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                data-testid={`search-spotlight-tab-${tab.id}`}
                data-category={tab.category}
                className={`search-dropdown-tab${selected ? ' is-active' : ''}`}
                onMouseDown={event => event.preventDefault()}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.showDot ? (
                  <span className={`search-dropdown-tab__dot${selected ? ' is-active' : ''}`} aria-hidden />
                ) : null}
                <span>
                  {tab.label}{' '}
                  <span className={`search-dropdown-section__total-count${selected ? ' is-active' : ''}`}>
                    {tab.count}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div ref={listRef} className="search-dropdown-body" role="listbox" aria-label="Search suggestions">
        {isLoading && noData ? (
          <span className="search-dropdown-status">Loading...</span>
        ) : error ? (
          <span className="search-dropdown-status">
            {error.includes('403') ? 'Session expired. Please refresh page' : `Error: ${error}`}
          </span>
        ) : noData ? (
          <span className="search-dropdown-status">No results found</span>
        ) : (
          visibleSections.map(section => {
            const items = section.preview.map((item, index) => {
              const navIndex = navCursor;
              navCursor += 1;
              const patched = patchItems(item);
              return (
                <SearchSpotlightItem
                  key={patched.id ?? `${section.title}-${index}`}
                  item={item}
                  query={trimmed}
                  isFocused={focusedIndex === navIndex}
                  isSelected={selectedItems.some(selected => selected.id === patched.id)}
                  optionId={`search-spotlight-option-${navIndex}`}
                  navIndex={navIndex}
                  onToggle={toggleItem}
                  onGoTo={goToItem}
                />
              );
            });
            const seeAllIndex = section.truncated ? navCursor : null;
            if (section.truncated) navCursor += 1;

            return (
              <section
                key={section.title}
                className="search-dropdown-section"
                data-category={section.title}
                aria-labelledby={`search-spotlight-heading-${section.title}`}
              >
                <div className="search-dropdown-section__header">
                  <div className="search-dropdown-section__heading">
                    <CategoryIcon category={section.title} />
                    <h3 id={`search-spotlight-heading-${section.title}`}>
                      {section.title}{' '}
                      <span className="search-dropdown-section__total">
                        {resolvedTab === ALL_RESULTS_TAB
                          ? `${section.preview.length} of ${section.total}`
                          : section.total}
                      </span>
                    </h3>
                  </div>
                  {section.truncated ? (
                    <>
                      <span className="search-dropdown-section__divider" aria-hidden />
                      <button
                        type="button"
                        className={`search-dropdown-see-all${focusedIndex === seeAllIndex ? ' is-focused' : ''}`}
                        data-testid={`search-spotlight-see-all-${section.title}`}
                        data-nav-index={seeAllIndex ?? undefined}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => setActiveTab(section.title)}
                      >
                        See all {section.total}
                      </button>
                    </>
                  ) : null}
                </div>
                <ul className="react-dropdown-list">{items}</ul>
              </section>
            );
          })
        )}
      </div>
      {footer}
    </div>
  );
}
